import { FC, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  IconButton,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Box,
  InputAdornment
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import theme from 'theme'

/* Utils */
import { ConnectionI, UserRoleSummary } from '../../../../../models'
import axios from '../../../../../lib/axios'
import { useAppDispatch } from '../../../../../store/hooks'
import { openToast, closeToast, ToastVariant } from '../../../../../store/toasts/toastSlice'

/* Components */
import { SSOTooltip } from './SSOTooltip'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  FormField,
  TextAreaField,
  ReadOnlyField,
  CancelButton,
  SaveButton,
  DeleteDomainButton
} from './SSOConnectionDrawer.styles'
import { PendingBadge } from './SSOsupport.styles'
import OnyxiaBasicModal from '../../../../components/modal/OnyxiaBasicModal'
import LockIcon from '../../../../components/svg/LockIcon'
import { getAcsUrl } from '../../settings.utils'

// The API keys an SSO connection by its domain (see the PUT and DELETE payloads), so an existing
// connection's domain cannot be changed — delete and re-create instead.
const DOMAIN_LOCKED_HELPER_TEXT = "Domain can't be changed after the connection is created."

const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/
const URL_REGEX = /^https:\/\/[a-zA-Z0-9](?:[a-zA-Z0-9-.]*[a-zA-Z0-9])(?::\d+)?(?:\/\S*)?$/

const validateDomain = (value: string): string => {
  if (!value.trim()) return 'Domain name is required'
  if (!DOMAIN_REGEX.test(value.trim())) return 'Enter a valid domain (e.g. company.com)'
  return ''
}

const validateSsoUrl = (value: string): string => {
  if (!value.trim()) return 'SSO URL is required'
  if (!URL_REGEX.test(value.trim())) return 'Enter a valid HTTPS URL (e.g. https://idp.example.com/sso)'
  return ''
}

const validateCertificate = (value: string): string => {
  if (!value.trim()) return 'Public certificate is required'
  if (value.trim().length < 60) return 'Certificate appears too short. Paste the full public certificate.'
  return ''
}

interface FieldErrors {
  domain: string
  ssoUrl: string
  publicCertificate: string
}

interface Props {
  open: boolean
  onClose: () => void
  connection: ConnectionI | null
  roles: UserRoleSummary[]
  onSuccess: () => void
}

const SSOConnectionDrawer: FC<Props> = ({ open, onClose, connection, roles, onSuccess }) => {
  const dispatch = useAppDispatch()
  const [isEdit, setIsEdit] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [initialValues, setInitialValues] = useState({ domain: '', ssoUrl: '', publicCertificate: '', userRoleId: '' })
  const [acsUrl, setAcsUrl] = useState('')
  const [entityId, setEntityId] = useState('')

  const [domain, setDomain] = useState(connection?.domain || '')
  const [ssoUrl, setSsoUrl] = useState(connection?.sso_url || '')
  const [publicCertificate, setPublicCertificate] = useState(connection?.public_certificate || '')
  const [userRoleId, setUserRoleId] = useState(connection?.default_user_role_id || '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDomainChangeConfirmOpen, setIsDomainChangeConfirmOpen] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [touched, setTouched] = useState({ domain: false, ssoUrl: false, publicCertificate: false })
  const [errors, setErrors] = useState<FieldErrors>({ domain: '', ssoUrl: '', publicCertificate: '' })

  const handleBlur = (field: keyof FieldErrors) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const validators: Record<keyof FieldErrors, (v: string) => string> = {
      domain: validateDomain,
      ssoUrl: validateSsoUrl,
      publicCertificate: validateCertificate
    }
    const value = field === 'domain' ? domain : field === 'ssoUrl' ? ssoUrl : publicCertificate
    setErrors((prev) => ({ ...prev, [field]: validators[field](value) }))
  }

  useEffect(() => {
    if (open) {
      const acsUrl = getAcsUrl()
      setIsEdit(!!connection)
      setIsPending(!!connection && !connection.verified)
      setDomain(connection?.domain || '')
      setSsoUrl(connection?.sso_url || '')
      setPublicCertificate(connection?.public_certificate || '')
      setUserRoleId(connection?.default_user_role_id || '')
      setAcsUrl(acsUrl)
      setEntityId(connection?.entity_id || process.env.REACT_APP_FRONTEGG_SAML_SP_ENTITY_ID || '')
      setInitialValues({
        domain: connection?.domain || '',
        ssoUrl: connection?.sso_url || '',
        publicCertificate: connection?.public_certificate || '',
        userRoleId: connection?.default_user_role_id || ''
      })
      setCopiedField(null)
      setIsDomainChangeConfirmOpen(false)
      setTouched({ domain: false, ssoUrl: false, publicCertificate: false })
      setErrors({ domain: '', ssoUrl: '', publicCertificate: '' })
    }
  }, [open, connection])

  const handleCopy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (e) {
      console.error('Failed to copy:', e)
    }
  }

  const persistChanges = async () => {
    setIsSubmitting(true)
    try {
      const payload = {
        domain,
        sso_url: ssoUrl,
        public_certificate: publicCertificate,
        default_user_role_id: userRoleId
      }

      if (isEdit && initialValues.domain) {
        await axios.put('/api/v3/sso', { ...payload, domain: initialValues.domain })
        dispatch(
          openToast({
            variant: ToastVariant.success,
            props: {
              text: 'SSO connection updated',
              description: 'Connection has been updated successfully.'
            }
          })
        )
      } else {
        const { data: response } = await axios.post('/api/v3/sso', payload)
        const autoApproved =
          response?.outcome === 'auto-approved' || response?.data?.verified === true
        dispatch(
          openToast({
            variant: ToastVariant.success,
            props: autoApproved
              ? {
                  text: 'SSO connection added',
                  description: 'Your SSO connection is active.'
                }
              : {
                  text: 'SSO connection submitted',
                  description: 'Your request has been submitted for approval.'
                }
          })
        )
      }
      setTimeout(() => dispatch(closeToast()), 5000)
      onSuccess()
      onClose()
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Something went wrong'
      const failureText = isEdit ? 'Failed to update SSO connection' : 'Failed to add SSO connection'
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: failureText,
            description: message
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    const domainError = validateDomain(domain)
    const ssoUrlError = validateSsoUrl(ssoUrl)
    const certError = validateCertificate(publicCertificate)
    setErrors({ domain: domainError, ssoUrl: ssoUrlError, publicCertificate: certError })
    setTouched({ domain: true, ssoUrl: true, publicCertificate: true })
    if (domainError || ssoUrlError || certError) return

    // Unreachable while the domain field is locked; a pending rename also needs API support first,
    // since PUT /sso keys on domain and so resubmits the original value.
    if (isEdit && isPending && domain.trim().toLowerCase() !== initialValues.domain.trim().toLowerCase()) {
      setIsDomainChangeConfirmOpen(true)
      return
    }

    await persistChanges()
  }

  const handleConfirmDomainChange = async () => {
    setIsDomainChangeConfirmOpen(false)
    await persistChanges()
  }

  const handleDelete = async () => {
    if (!initialValues.domain) return
    setIsDeleting(true)
    try {
      await axios.delete('/api/v3/sso', { data: { domain: initialValues.domain } })
      setIsDeleteModalOpen(false)
      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: { text: 'SSO connection deleted', description: 'Connection has been deleted successfully.' }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
      onSuccess()
      onClose()
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Something went wrong'
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: { text: 'Failed to delete SSO connection', description: message }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    } finally {
      setIsDeleting(false)
    }
  }

  const isDomainLocked = isEdit
  const isFormValid =
    domain && ssoUrl && publicCertificate && userRoleId && !errors.domain && !errors.ssoUrl && !errors.publicCertificate
  const hasChanges = isEdit
    ? domain !== initialValues.domain ||
      ssoUrl !== initialValues.ssoUrl ||
      publicCertificate !== initialValues.publicCertificate ||
      userRoleId !== initialValues.userRoleId
    : true

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <DrawerContent>
          <DrawerHeader>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography className="drawer-title">{isEdit ? 'Edit Connection' : 'Add New Connection'}</Typography>
              {isPending ? <PendingBadge>Pending Approval</PendingBadge> : null}
            </Box>
            <IconButton className="close-button" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 18L18 6M6 6L18 18"
                  stroke="#E9E0E4"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          </DrawerHeader>

          <DrawerBody>
            <FormField className={isDomainLocked ? 'locked-field' : undefined}>
              <TextField
                fullWidth
                label="Domain name"
                placeholder="Enter your company's domain"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value)
                  if (touched.domain) setErrors((prev) => ({ ...prev, domain: validateDomain(e.target.value) }))
                }}
                onBlur={() => handleBlur('domain')}
                helperText={isDomainLocked ? DOMAIN_LOCKED_HELPER_TEXT : touched.domain && errors.domain}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: isDomainLocked,
                  endAdornment: isDomainLocked ? (
                    <InputAdornment position="end">
                      <LockIcon />
                    </InputAdornment>
                  ) : undefined
                }}
              />
            </FormField>

            <FormField>
              <TextField
                fullWidth
                label="SSO URL"
                placeholder="Enter your identity provider SSO URL"
                value={ssoUrl}
                onChange={(e) => {
                  setSsoUrl(e.target.value)
                  if (touched.ssoUrl) setErrors((prev) => ({ ...prev, ssoUrl: validateSsoUrl(e.target.value) }))
                }}
                onBlur={() => handleBlur('ssoUrl')}
                helperText={touched.ssoUrl && errors.ssoUrl}
                InputLabelProps={{ shrink: true }}
              />
              <SSOTooltip title="Identify Provider Single Sign-On (SSO) URL." placement="top-start">
                <span className="info-icon info-icon-sso">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.5 7.5L7.52766 7.48617C7.90974 7.29513 8.33994 7.64023 8.23634 8.05465L7.76366 9.94535C7.66006 10.3598 8.09026 10.7049 8.47234 10.5138L8.5 10.5M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8ZM8 5.5H8.005V5.505H8V5.5Z"
                      stroke="#6C5B67"
                      strokeWidth="1.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </SSOTooltip>
            </FormField>

            <TextAreaField>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Public certificate"
                placeholder="Paste your public certificate"
                value={publicCertificate}
                onChange={(e) => {
                  setPublicCertificate(e.target.value)
                  if (touched.publicCertificate)
                    setErrors((prev) => ({ ...prev, publicCertificate: validateCertificate(e.target.value) }))
                }}
                onBlur={() => handleBlur('publicCertificate')}
                helperText={touched.publicCertificate && errors.publicCertificate}
                InputLabelProps={{ shrink: true }}
              />
            </TextAreaField>

            <ReadOnlyField>
              <div className="field-content">
                <Typography className="field-label">ACS URL</Typography>
                <Typography className="field-value">{acsUrl}</Typography>
              </div>
              <SSOTooltip title={copiedField === 'acs' ? 'Copied!' : 'Copy to clipboard'} placement="top">
                <IconButton className="copy-button" onClick={() => handleCopy(acsUrl, 'acs')}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6 6V3.9C6 3.05992 6 2.63988 6.16349 2.31901C6.3073 2.03677 6.53677 1.8073 6.81901 1.66349C7.13988 1.5 7.55992 1.5 8.4 1.5H14.1C14.9401 1.5 15.3601 1.5 15.681 1.66349C15.9632 1.8073 16.1927 2.03677 16.3365 2.31901C16.5 2.63988 16.5 3.05992 16.5 3.9V9.6C16.5 10.4401 16.5 10.8601 16.3365 11.181C16.1927 11.4632 15.9632 11.6927 15.681 11.8365C15.3601 12 14.9401 12 14.1 12H12M3.9 16.5H9.6C10.4401 16.5 10.8601 16.5 11.181 16.3365C11.4632 16.1927 11.6927 15.9632 11.8365 15.681C12 15.3601 12 14.9401 12 14.1V8.4C12 7.55992 12 7.13988 11.8365 6.81901C11.6927 6.53677 11.4632 6.3073 11.181 6.16349C10.8601 6 10.4401 6 9.6 6H3.9C3.05992 6 2.63988 6 2.31901 6.16349C2.03677 6.3073 1.8073 6.53677 1.66349 6.81901C1.5 7.13988 1.5 7.55992 1.5 8.4V14.1C1.5 14.9401 1.5 15.3601 1.66349 15.681C1.8073 15.9632 2.03677 16.1927 2.31901 16.3365C2.63988 16.5 3.05992 16.5 3.9 16.5Z"
                      stroke="#A88CA4"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </SSOTooltip>
            </ReadOnlyField>

            <ReadOnlyField>
              <div className="field-content">
                <Typography className="field-label">SP Entity ID</Typography>
                <Typography className="field-value">{entityId}</Typography>
              </div>
              <SSOTooltip title={copiedField === 'entity' ? 'Copied!' : 'Copy to clipboard'} placement="top">
                <IconButton className="copy-button" onClick={() => handleCopy(entityId, 'entity')}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6 6V3.9C6 3.05992 6 2.63988 6.16349 2.31901C6.3073 2.03677 6.53677 1.8073 6.81901 1.66349C7.13988 1.5 7.55992 1.5 8.4 1.5H14.1C14.9401 1.5 15.3601 1.5 15.681 1.66349C15.9632 1.8073 16.1927 2.03677 16.3365 2.31901C16.5 2.63988 16.5 3.05992 16.5 3.9V9.6C16.5 10.4401 16.5 10.8601 16.3365 11.181C16.1927 11.4632 15.9632 11.6927 15.681 11.8365C15.3601 12 14.9401 12 14.1 12H12M3.9 16.5H9.6C10.4401 16.5 10.8601 16.5 11.181 16.3365C11.4632 16.1927 11.6927 15.9632 11.8365 15.681C12 15.3601 12 14.9401 12 14.1V8.4C12 7.55992 12 7.13988 11.8365 6.81901C11.6927 6.53677 11.4632 6.3073 11.181 6.16349C10.8601 6 10.4401 6 9.6 6H3.9C3.05992 6 2.63988 6 2.31901 6.16349C2.03677 6.3073 1.8073 6.53677 1.66349 6.81901C1.5 7.13988 1.5 7.55992 1.5 8.4V14.1C1.5 14.9401 1.5 15.3601 1.66349 15.681C1.8073 15.9632 2.03677 16.1927 2.31901 16.3365C2.63988 16.5 3.05992 16.5 3.9 16.5Z"
                      stroke="#A88CA4"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </SSOTooltip>
            </ReadOnlyField>

            <FormField>
              <FormControl fullWidth>
                <InputLabel shrink>User roles</InputLabel>
                <Select
                  value={userRoleId}
                  onChange={(e) => setUserRoleId(e.target.value)}
                  displayEmpty
                  IconComponent={KeyboardArrowDownIcon}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: theme.colors.menuBg,
                        borderRadius: '12px',
                        maxHeight: 300,
                        "html[data-theme='light'] &": {
                          background: theme.colors.surfaceContainerBackground
                        },
                        '& .MuiMenuItem-root': {
                          fontFamily: 'Quicksand, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: theme.colors.menuItemText,
                          padding: '10px 16px',
                          '&:hover': {
                            background: 'rgba(255, 169, 252, 0.08)',
                            "html[data-theme='light'] &": {
                              background: theme.colors.menuItemHover
                            }
                          },
                          '&.Mui-selected': {
                            background: 'rgba(255, 169, 252, 0.12)',
                            "html[data-theme='light'] &": {
                              background: theme.colors.menuItemHover
                            },
                            '&:hover': {
                              background: 'rgba(255, 169, 252, 0.16)',
                              "html[data-theme='light'] &": {
                                background: theme.colors.menuItemHover
                              }
                            }
                          }
                        }
                      }
                    }
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: theme.colors.devicesSecondaryText }}>Choose from the list</span>
                    }
                    const selectedRole = roles.find((r) => r.id === selected)
                    return selectedRole?.name || selected
                  }}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SSOTooltip title="The default role will be assigned to all SSO users." placement="top-start">
                <span className="info-icon info-icon-roles">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.5 7.5L7.52766 7.48617C7.90974 7.29513 8.33994 7.64023 8.23634 8.05465L7.76366 9.94535C7.66006 10.3598 8.09026 10.7049 8.47234 10.5138L8.5 10.5M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8ZM8 5.5H8.005V5.505H8V5.5Z"
                      stroke="#6C5B67"
                      strokeWidth="1.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </SSOTooltip>
              <Typography className="field-subtitle">Applies to new SSO sign-ins only.</Typography>
            </FormField>
          </DrawerBody>

          <DrawerFooter sx={isEdit ? { justifyContent: 'space-between' } : undefined}>
            {isEdit ? (
              <>
                <DeleteDomainButton onClick={() => setIsDeleteModalOpen(true)} sx={{ width: 146 }}>
                  <Typography className="button-text">Delete Domain</Typography>
                </DeleteDomainButton>
                <div style={{ display: 'flex', gap: 8 }}>
                  <CancelButton onClick={onClose} sx={{ flex: 'unset', width: 93, minWidth: 93 }}>
                    <Typography className="button-text">Cancel</Typography>
                  </CancelButton>
                  <SaveButton
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormValid || !hasChanges}
                    sx={{ flex: 'unset', width: 80 }}
                  >
                    <Typography className="button-text">Save</Typography>
                  </SaveButton>
                </div>
              </>
            ) : (
              <>
                <CancelButton onClick={onClose}>
                  <Typography className="button-text">Cancel</Typography>
                </CancelButton>
                <SaveButton onClick={handleSubmit} disabled={isSubmitting || !isFormValid}>
                  <Typography className="button-text">Add</Typography>
                </SaveButton>
              </>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {isDeleteModalOpen &&
        createPortal(
          <OnyxiaBasicModal
            isOpen={isDeleteModalOpen}
            title={`Delete SSO connection for ${domain}`}
            description={
              <>
                Are you sure you want to delete this connection?
                <br />
                Removing the SSO connection for <strong style={{ color: theme.colors.menuItemText }}>{domain}</strong> will require
                users to sign in using email and password. Users who do not have existing credentials will need to be
                re-invited.
              </>
            }
            submitButtonText="Delete"
            cancelButtonText="Cancel"
            onSubmit={handleDelete}
            onCancel={() => setIsDeleteModalOpen(false)}
            submitDisabled={isDeleting}
          />,
          document.body
        )}

      {isDomainChangeConfirmOpen &&
        createPortal(
          <OnyxiaBasicModal
            isOpen={isDomainChangeConfirmOpen}
            title="Re-submit for approval?"
            description={
              <>
                Changing the domain on a pending request resubmits this connection from scratch.
                <br />
                Any position in the existing approval queue for{' '}
                <strong style={{ color: theme.colors.menuItemText }}>{initialValues.domain}</strong> will be lost.
              </>
            }
            submitButtonText="Re-submit"
            cancelButtonText="Cancel"
            onSubmit={handleConfirmDomainChange}
            onCancel={() => setIsDomainChangeConfirmOpen(false)}
            submitDisabled={isSubmitting}
          />,
          document.body
        )}
    </>
  )
}

export default SSOConnectionDrawer
